import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, PenLine } from "lucide-react";
import { scrollToId } from "@/lib/smoothScroll";

const LINKS = [
  { id: "about", label: "Manifesto", testid: "nav-about-link" },
  { id: "work", label: "Work", testid: "nav-samples-link" },
  { id: "journal", label: "Journal", testid: "nav-journal-link" },
  { id: "services", label: "Services", testid: "nav-services-link" },
  { id: "voices", label: "Voices", testid: "nav-voices-link" },
  { id: "contact", label: "Contact", testid: "nav-contact-link" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-paper/85 backdrop-blur-md border-ink/10 shadow-[0_1px_24px_rgba(26,24,21,0.06)]"
          : "bg-transparent border-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 h-[72px] flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          data-testid="nav-home-link"
        >
          <span className="w-9 h-9 rounded-full bg-wine text-paper flex items-center justify-center transition-transform duration-500 group-hover:rotate-[360deg]">
            <PenLine size={15} strokeWidth={1.75} />
          </span>
          <span className="font-serif text-lg tracking-tight">
            Melinda <em className="not-italic text-wine">Adams</em>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={l.testid}
              className="relative font-mono text-[11px] uppercase tracking-[0.2em] text-subtle hover:text-ink transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-wine after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            data-testid="nav-enquiry-button"
            className="ml-2 px-5 py-2.5 rounded-full bg-ink text-paper font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-wine transition-colors duration-300"
          >
            Start an Enquiry
          </button>
        </nav>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-paper/95 backdrop-blur-md border-b border-ink/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-${l.id}-link`}
                  className="text-left font-serif text-2xl tracking-tight"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
